'use strict';
import http from 'http';
import https from 'https';
import path from 'path';
import { v4 as uuidv4 } from 'uuid';
import _isEmpty from 'lodash/isEmpty';
import { AuthenticationError, ConnectionError, generate, LalamoveError, RateLimitError } from './Errors';
import { makeURLInterpolator, normalizeHeaders, protoExtend, removeNullish, emitInfo, computeHmacSha256, } from './utils';
const defaultHttpAgent = new http.Agent({ keepAlive: true });
const defaultHttpsAgent = new https.Agent({ keepAlive: true });
LalamoveResource.extend = protoExtend;
export default function LalamoveResource(lalamove) {
    this.lalamove = lalamove;
    this.basePath = makeURLInterpolator(this.basePath || lalamove.getApiField('basePath'));
    this.resourcePath = this.path;
    this.path = makeURLInterpolator(this.path);
    this.initialize(...arguments);
}
LalamoveResource.prototype = {
    path: '',
    basePath: null,
    initialize() { },
    createFullPath(commandPath, urlData) {
        return path
            .join(this.basePath(urlData), this.path(urlData), typeof commandPath == 'function' ? commandPath(urlData) : commandPath)
            .replace(/\\/g, '/');
    },
    createResourcePathWithSymbols(pathWithSymbols) {
        return `/${path.join(this.resourcePath, pathWithSymbols || '').replace(/\\/g, '/')}`;
    },
    timeoutHandler(timeout, req, callback) {
        return () => {
            const timeoutErr = new TypeError('ETIMEDOUT');
            timeoutErr.code = 'ETIMEDOUT';
            req._isAborted = true;
            req.abort();
            callback.call(this, new ConnectionError({
                message: `Request aborted due to timeout being reached (${timeout}ms)`,
                detail: timeoutErr,
            }), null);
        };
    },
    responseHandler(req, callback) {
        return res => {
            let response = '';
            res.setEncoding('utf8');
            res.on('data', chunk => response += chunk);
            res.once('end', () => {
                const requestEndTime = Date.now();
                const requestDurationMs = requestEndTime - req._requestStart;
                const responseEvent = removeNullish({
                    method: req._requestEvent.method,
                    path: req._requestEvent.path,
                    status: res.statusCode,
                    elapsed: requestDurationMs,
                    requestStartTime: req._requestStart,
                    requestEndTime,
                });
                this.lalamove._emitter.emit('response', responseEvent);
                try {
                    emitInfo(`Response: ${response}`);
                    response = JSON.parse(response);
                    if ((res.statusCode >= 400 && res.statusCode < 500) || res.statusCode >= 500) {
                        let err;
                        if (res.statusCode === 401) {
                            err = new AuthenticationError(response);
                        }
                        else if (res.statusCode === 429) {
                            err = new RateLimitError(response);
                        }
                        else {
                            err = generate(response);
                        }
                        return callback.call(this, err, null);
                    }
                }
                catch (e) {
                    return callback.call(this, new LalamoveError({ message: 'Invalid JSON received from the Lalamove API' }), null);
                }
                callback.call(this, null, response);
            });
        };
    },
    errorHandler(req, callback) {
        return error => {
            if (req._isAborted)
                return;
            callback.call(this, new ConnectionError({
                message: 'An error occurred with our connection to Lalamove.',
                detail: error,
            }), null);
        };
    },
    makeHeaders(auth, userSuppliedHeaders) {
        const requestID = uuidv4();
        const market = this.lalamove.getApiField('market') || '';
        const defaultHeaders = {
            'Authorization': auth,
            'Market': market,
            'X-Request-ID': requestID,
            'Accept': 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
        };
        return Object.assign(removeNullish(defaultHeaders), normalizeHeaders(userSuppliedHeaders));
    },
    makeRequest(data, path, method, headers, options, callback) {
        const requestStartTime = Date.now();
        let timeout = this.lalamove.getApiField('timeout');
        if (options.settings && Number.isInteger(options.settings.timeout) && options.settings.timeout >= 0) {
            timeout = options.settings.timeout;
        }
        const insecureConnection = this.lalamove.getApiField('protocol') === 'http';
        const agent = this.lalamove.getApiField('agent') || (insecureConnection ? defaultHttpAgent : defaultHttpsAgent);
        const req = (insecureConnection ? http : https).request({
            host: this.lalamove.getApiField('host'),
            port: this.lalamove.getApiField('port'),
            path,
            method,
            agent,
            headers,
        });
        const requestEvent = removeNullish({ method, path, requestStartTime });
        req._requestEvent = requestEvent;
        req._requestStart = requestStartTime;
        this.lalamove._emitter.emit('request', requestEvent);
        req.setTimeout(timeout, this.timeoutHandler(timeout, req, callback));
        req.once('response', response => this.responseHandler(req, callback)(response));
        req.on('error', error => this.errorHandler(req, callback)(error));
        req.once('socket', socket => {
            if (socket.connecting) {
                socket.once(insecureConnection ? 'connect' : 'secureConnect', () => {
                    req.write(data);
                    req.end();
                });
            }
            else {
                req.write(data);
                req.end();
            }
        });
    },
    request(method, path, data, options = {}, callback) {
        const body = JSON.stringify(data);
        emitInfo(`Request Path: ${path}`);
        emitInfo(`Request Data: ${body}`);
        const timeVariance = Date.now().toString();
        let rawSignature = `${timeVariance}\r\n${method}\r\n${path}\r\n\r\n`;
        if (!_isEmpty(data)) {
            rawSignature = `${rawSignature}${body}`;
        }
        const signature = computeHmacSha256(rawSignature, this.lalamove.getApiField('apiSecret'));
        const auth = `hmac ${this.lalamove.getApiField('apiKey')}:${timeVariance}:${signature}`;
        const prepareAndMakeRequest = (error, data) => {
            if (error)
                return callback(error);
            return this.makeRequest(data, path, method, this.makeHeaders(auth, options.headers), options, callback);
        };
        prepareAndMakeRequest(null, body);
    },
};
//# sourceMappingURL=LalamoveResource.js.map